import React, { Suspense } from 'react';
import { apm } from '@elastic/apm-rum';
import { Button, Icon, Typography } from 'glints-aries/es/@next';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { UnverifiedCompanyType } from '../../common/constants';
import { useUnverifiedCompanyRestrictionModal } from '../../common/hooks/useUnverifiedCompanyRestrictionModal/useUnverifiedCompanyRestrictionModal';
import ChatButton from '../../components/ChatButton';
import MobileChatButton from '../../components/ChatButton/MobileChatButton';
import ErrorBoundaryWithCrashReporting from '../../components/Error/ErrorBoundaryWithCrashReporting';
import * as employerAtsTracking from '../EmployerATS/tracking';
import { trackChatPageViewedEvent } from '../GlintsChat/Channel/components/ChannelDetail/ChannelHeader/tracking';
import { MessagingUIMode } from '../GlintsChat/constants';
import { useIsMobileVersion } from '../GlintsChat/hooks/useIsMobileVersion';
import useGetApproximateTotalUnreadCount from './hooks/useGetApproximateTotalUnreadCount';

type CTAChatProps = {
  compact?: boolean;
};

const CTAChatComponent = ({ compact }: CTAChatProps) => {
  const { totalGlobalUnreadCount } = useGetApproximateTotalUnreadCount();
  const isMobileVersion = useIsMobileVersion('TopNavCTA');
  const { getOpenRestrictionModalFunc } = useUnverifiedCompanyRestrictionModal({
    type: UnverifiedCompanyType.APPLICATION,
  });
  const openRestrictionModalFunc = getOpenRestrictionModalFunc();
  return (
    <Link
      data-cy="chat-cta-menu"
      to="/messages"
      onClick={e => {
        if (openRestrictionModalFunc) {
          e.preventDefault();
          openRestrictionModalFunc();
          return;
        }
        employerAtsTracking.trackGlobalChatClickedEvent(
          employerAtsTracking.ChatClickedEventLocation.HEADER,
          totalGlobalUnreadCount
        );
        trackChatPageViewedEvent({
          source: 'navbar',
          uiType: MessagingUIMode.FULL,
        });
      }}
    >
      <Choose>
        <When condition={isMobileVersion}>
          <MobileChatButton unreadCount={totalGlobalUnreadCount} />
        </When>
        <Otherwise>
          <ChatButton
            size={compact ? 'slim' : 'default'}
            unreadCount={totalGlobalUnreadCount}
            icon={<Icon name="ri-message-line" />}
            iconPosition="left"
            style={{
              minHeight: 32,
              textTransform: 'uppercase',
            }}
          />
        </Otherwise>
      </Choose>
    </Link>
  );
};

const CTAChatLoading = ({ compact }: CTAChatProps) => (
  <Button
    loading={true}
    size={compact ? 'slim' : 'default'}
    style={{ minHeight: 32 }}
  >
    <Typography variant="button">
      <FormattedMessage id="button-text_chat" defaultMessage="Chat" />
    </Typography>
  </Button>
);

const CTAChat = ({ compact }: CTAChatProps) => (
  <ErrorBoundaryWithCrashReporting
    onError={e => {
      apm.setCustomContext({ ctaChatNavbar: 'error' });
      apm.captureError(e);
      apm.setCustomContext({ ctaChatNavbar: '' });
    }}
    fallback={null}
  >
    <Suspense fallback={<CTAChatLoading compact={compact} />}>
      <CTAChatComponent compact={compact} />
    </Suspense>
  </ErrorBoundaryWithCrashReporting>
);

export default CTAChat;
